import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import {
  ATTENDANCE_TIMING_TAGS,
  getMissingAgentAbsenceNote,
  getMissingAgentChipLabel,
  getMissingAgentChipSx,
  getMissingAgentSecondaryText,
  getMissingAgentShiftPreview,
  MissingAgentRow,
} from '../../utils/attendanceLabels';
import { getApprovedAbsenceDisplayLabel } from '../../constants/absenceReasons';
import moment from 'moment-timezone';

export interface MissingAgentsShareContentProps {
  agents: MissingAgentRow[];
  workDate: string;
  generatedAt?: string;
  width?: number | string;
  forExport?: boolean;
}

const MissingAgentsShareContent = React.forwardRef<HTMLDivElement, MissingAgentsShareContentProps>(({
  agents,
  workDate,
  generatedAt,
  width = 720,
  forExport = false,
}, ref) => {
  const generatedLabel = generatedAt
    ? moment(generatedAt).format('MMM D, YYYY h:mm A')
    : moment().format('MMM D, YYYY h:mm A');

  return (
    <Box
      ref={ref}
      sx={{
        width,
        maxWidth: '100%',
        mx: 'auto',
        bgcolor: forExport ? '#f4f6f8' : 'transparent',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <Box sx={{ bgcolor: '#1a237e', color: '#fff', borderRadius: '12px 12px 0 0', px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>TEKS TIME</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 0.5 }}>Missing Agents</Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.92, mt: 1 }}>{workDate}</Typography>
      </Box>

      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #e0e0e0',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          px: 2.5,
          py: 2.25,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Generated {generatedLabel}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1a237e', mt: 0.5 }}>
              {agents.length} missing agent{agents.length === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {ATTENDANCE_TIMING_TAGS.map((tag) => (
            <Chip
              key={tag.key}
              label={tag.label}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: tag.color,
                color: tag.textColor,
              }}
            />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', alignSelf: 'center' }}>
            (auto unless supervisor approves absence)
          </Typography>
        </Stack>

        {agents.map((agent) => {
          const chipLabel = getMissingAgentChipLabel(agent);
          const chipSx = getMissingAgentChipSx(agent);
          const shiftPreview = getMissingAgentShiftPreview(agent);
          const secondary = getMissingAgentSecondaryText(agent);
          const absenceNote = getMissingAgentAbsenceNote(agent);
          const absenceTypeLabel = agent.status === 'absent_approved' && (agent.absenceType || agent.absenceReason)
            ? getApprovedAbsenceDisplayLabel({
                absenceType: agent.absenceType,
                reason: agent.absenceReason,
              })
            : null;

          return (
            <Box
              key={agent.id}
              sx={{
                border: '1px solid #e0e0e0',
                borderLeft: `4px solid ${chipSx.bgcolor}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1.25,
                bgcolor: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1a237e', lineHeight: 1.3 }}>
                    {agent.name}
                  </Typography>
                  {agent.employeeId && (
                    <Typography variant="caption" color="text.secondary">
                      ID {agent.employeeId}
                    </Typography>
                  )}
                </Box>
                <Chip label={chipLabel} size="small" sx={{ ...chipSx, fontWeight: 700, flexShrink: 0 }} />
              </Box>
              <Box sx={{ mt: 1, fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                <div><strong>Campaign:</strong> {agent.campaign}</div>
                <div><strong>Department:</strong> {agent.department}</div>
                <div><strong>Scheduled:</strong> {shiftPreview || 'Not scheduled'}</div>
                <div><strong>Status:</strong> {absenceNote && absenceTypeLabel ? absenceTypeLabel : secondary}</div>
                {absenceNote && (
                  <div><strong>Reason:</strong> {absenceNote}</div>
                )}
                {!absenceNote && agent.absenceType && (
                  <div><strong>Absence type:</strong> {agent.absenceType}</div>
                )}
              </Box>
            </Box>
          );
        })}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2, pt: 1.5, borderTop: '1px solid #eee' }}
        >
          Generated from TEKS TIME Dashboard
        </Typography>
      </Box>
    </Box>
  );
});

MissingAgentsShareContent.displayName = 'MissingAgentsShareContent';

export default MissingAgentsShareContent;
